import {
  convertGqlGenderToSex,
  type PlannerProfileResponse,
  type SpecialtiesResponse,
} from '@/apis/repositories/models/superfrog.ts'
import { parseSpecialtiesResponseToMatchingFilters } from '@/apis/repositories/parsers/matchingFilter'
import { createAvatarFromImgixUrl } from '@/apis/repositories/parsers/plannerAvatar'
import { parseSpecialtiesResponseToSpecialties } from '@/apis/repositories/parsers/specialty'
import { getAPIBaseURL } from '@/config'
import type { FlexImage } from '@/libs/utilities/FlexImage'
import { generateFlexImage } from '@/libs/utilities/FlexImage'
import { type Account, type Authority, type Planner } from '@/types/app'
import * as jisx0402 from '@/features/prefecture/jisx0402.ts'
import type { Jisx0402Code } from '@/features/prefecture/jisx0402.ts'

export const parsePlannerProfileResponseToAccount = (
  planner: PlannerProfileResponse
): Account => ({
  plannerId: planner.id,
  authorities: parseAuthorities(planner.authorities),
})

export const parsePlannerProfileResponseToPlanner = (
  planner: PlannerProfileResponse,
  allSpecialties: SpecialtiesResponse
): Planner => {
  const specialtiesAndMatchingFilters = tagsToSpecialties(
    planner.specialties,
    allSpecialties
  )
  return {
    id: planner.id,
    corporationId: planner.corporationId,
    branchIds: planner.branchIds,
    name: planner.name,
    avatarImage: createAvatarFromImgixUrl(planner.avatarUrl),
    coverImage: planner.coverUrl
      ? createCoverImage(planner.coverUrl)
      : undefined,
    email: planner.email,
    sex: convertGqlGenderToSex(planner.gender),
    birthday: planner.birthday ? new Date(planner.birthday) : new Date(), // FIXME: 本来はnullの場合はnullを返すべきだが、基本的にはnullにならないので一旦今のまま
    availablePrefectureCodes: planner.availableAreaCodes
      .map(code => {
        const district = jisx0402.findByCode(code as Jisx0402Code)
        return district?.id ?? ''
      })
      .filter((code): code is Jisx0402Code => !!code),
    availableTimeText: planner.availableTime,
    selfIntroductionText: planner.comment,
    specialties: parseSpecialtiesResponseToSpecialties(
      specialtiesAndMatchingFilters
    ),
    matchingFilters: parseSpecialtiesResponseToMatchingFilters(
      specialtiesAndMatchingFilters
    ),
  }
}

const tagsToSpecialties = (
  specialtyNames: readonly ElementOf<SpecialtiesResponse>['name'][],
  allSpecialties: SpecialtiesResponse
): SpecialtiesResponse => {
  const nameEntityMapping = allSpecialties.reduce<
    Record<
      ElementOf<SpecialtiesResponse>['name'],
      ElementOf<SpecialtiesResponse>
    >
  >((map, item) => ({ ...map, [item.name]: item }), {})
  return specialtyNames.map(name => nameEntityMapping[name])
}

const imgixDomain = new URL(getAPIBaseURL('imgix')).host

// NOTE: backward compatibility for derek
const createCoverImage = (path: string): FlexImage =>
  generateFlexImage(imgixDomain, new URL(path).pathname)

const parseAuthorities = (authorities: string[]): Authority[] => {
  if (!authorities || authorities.length === 0) {
    return []
  }

  const ALL_AUTHORITIES: Authority[] = [
    'view/corporation/rooms',
    'view/corporation/report',
    'view/branch/rooms',
    'view/branch/report',
    'edit/corporation',
    'edit/branch',
  ]

  return authorities.filter(auth =>
    ALL_AUTHORITIES.includes(auth as Authority)
  ) as Authority[]
}
